import { useState } from 'react';
import { Head, router, Link } from '@inertiajs/react';
import { HealthBar } from '@/components/Adventures/HealthBar';
import { StageProgress } from '@/components/Adventures/StageProgress';
import {
    AdventureAttempt,
    HealingItem,
    HealingPhasePreview,
    HealingCharacterPreview,
    AdventureStageAttempt,
    GrantedReward,
} from '@/types/adventures';

interface Props {
    attempt: AdventureAttempt;
    healingPreview: HealingPhasePreview;
    healingItems: HealingItem[];
    stageAttempt: AdventureStageAttempt;
}

/**
 * HealingPhase page - shown between multi-stage adventure stages.
 * Allows players to use healing items and view passive healing before proceeding.
 */
export default function HealingPhase({
    attempt,
    healingPreview,
    healingItems,
    stageAttempt,
}: Props) {
    const [selectedItems, setSelectedItems] = useState<{ item_id: number; character_id: number }[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Group healing items by type for easier display
    const groupedItems = healingItems.reduce((acc, item) => {
        const key = item.name;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc;
    }, {} as Record<string, HealingItem[]>);

    // Check if an item is already assigned
    const isItemAssigned = (itemId: number) => {
        return selectedItems.some(s => s.item_id === itemId);
    };

    // Get assigned items for a character
    const getAssignedItems = (characterId: number) => {
        return selectedItems
            .filter(s => s.character_id === characterId)
            .map(s => healingItems.find(i => i.id === s.item_id)!)
            .filter(Boolean);
    };

    // Calculate total healing for a character (passive + assigned items)
    const getTotalHealing = (character: HealingCharacterPreview) => {
        const itemHealing = getAssignedItems(character.id)
            .reduce((sum, item) => sum + item.healing_amount, 0);
        return character.would_heal + itemHealing;
    };

    // Toggle item assignment to a character
    const toggleItemAssignment = (item: HealingItem, characterId: number) => {
        if (isItemAssigned(item.id)) {
            // Remove assignment
            setSelectedItems(prev => prev.filter(s => s.item_id !== item.id));
        } else {
            // Add assignment
            setSelectedItems(prev => [...prev, { item_id: item.id, character_id: characterId }]);
        }
    };

    // Apply healing and proceed to next stage
    const handleApplyHealing = () => {
        setIsSubmitting(true);
        router.post(`/adventures/attempts/${attempt.id}/apply-healing`, {
            items_to_use: selectedItems,
        }, {
            onFinish: () => setIsSubmitting(false),
        });
    };

    // Skip healing and go directly to next stage
    const handleSkipHealing = () => {
        setIsSubmitting(true);
        router.post(`/adventures/attempts/${attempt.id}/next-stage`, {}, {
            onFinish: () => setIsSubmitting(false),
        });
    };

    // Get rarity color
    const getRarityColor = (rarity: string) => {
        switch (rarity.toLowerCase()) {
            case 'common': return 'text-gray-300';
            case 'uncommon': return 'text-green-400';
            case 'rare': return 'text-blue-400';
            case 'epic': return 'text-purple-400';
            case 'legendary': return 'text-orange-400';
            default: return 'text-gray-300';
        }
    };

    return (
        <>
            <Head title="Healing Phase" />
            <div className="min-h-screen bg-slate-900 py-8 px-4">
                <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <Link
                        href={`/adventures/attempts/${attempt.id}`}
                        className="text-purple-400 hover:text-purple-300 text-sm"
                    >
                        ← Back to Adventure
                    </Link>
                    <h1 className="text-2xl font-bold text-white mt-2">Healing Phase</h1>
                    <p className="text-gray-400">
                        {attempt.adventure.name} - Stage {stageAttempt.stage_number} Complete
                    </p>
                </div>

                {/* Stage Progress */}
                <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <h2 className="text-sm font-medium text-gray-400 mb-3">Progress</h2>
                    <StageProgress
                        currentStage={attempt.current_stage}
                        totalStages={attempt.total_stages}
                        stageAttempts={attempt.stage_attempts}
                    />
                </div>

                {/* Stage Outcome */}
                <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <h2 className="text-sm font-medium text-gray-400 mb-2">Stage Outcome</h2>
                    <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            stageAttempt.status.value === 'success' ? 'bg-green-600 text-white' :
                            stageAttempt.status.value === 'partial' ? 'bg-yellow-600 text-white' :
                            'bg-red-600 text-white'
                        }`}>
                            {stageAttempt.status.label}
                        </span>
                        {stageAttempt.is_critical && (
                            <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded">
                                Critical!
                            </span>
                        )}
                        {stageAttempt.roll_result !== null && (
                            <span className="text-gray-400 text-sm">
                                Roll: {stageAttempt.roll_result} (needed ≤{stageAttempt.success_chance})
                            </span>
                        )}
                    </div>

                    {/* Stage Rewards */}
                    {stageAttempt.rewards_granted && stageAttempt.rewards_granted.length > 0 && (
                        <div className="mt-4">
                            <h3 className="text-sm font-medium text-green-400 mb-2">Rewards Earned</h3>
                            <div className="flex flex-wrap gap-2">
                                {stageAttempt.rewards_granted.map((reward: GrantedReward, index: number) => (
                                    <span key={index} className="px-2 py-1 bg-slate-700 rounded text-sm text-gray-300">
                                        {reward.type === 'xp' && `+${reward.total_xp} XP`}
                                        {reward.type === 'material' && `+${reward.quantity} ${reward.material_type}`}
                                        {reward.type === 'item' && reward.item_name}
                                        {reward.type === 'reputation' && `+${reward.amount} rep`}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Party Health */}
                <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-white">Party Health</h2>
                        {healingPreview.passive_per_character > 0 && (
                            <span className="text-green-400 text-sm">
                                +{healingPreview.passive_per_character} passive healing
                            </span>
                        )}
                    </div>

                    <div className="space-y-4">
                        {healingPreview.characters.map((character) => {
                            const assignedItems = getAssignedItems(character.id);
                            const totalHealing = getTotalHealing(character);
                            const projectedHealth = Math.min(
                                character.max_health,
                                character.current_health + totalHealing
                            );

                            return (
                                <div
                                    key={character.id}
                                    className={`p-4 rounded-lg ${
                                        character.is_incapacitated ? 'bg-slate-700/50' : 'bg-slate-700'
                                    }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <HealthBar
                                                health={{
                                                    current_health: character.current_health,
                                                    max_health: character.max_health,
                                                    health_percentage: character.health_percentage,
                                                    is_incapacitated: character.is_incapacitated,
                                                }}
                                                name={character.name}
                                                size="md"
                                            />

                                            {/* Healing preview */}
                                            {!character.is_incapacitated && totalHealing > 0 && (
                                                <div className="mt-2 text-sm">
                                                    <span className="text-gray-400">After healing: </span>
                                                    <span className="text-green-400">
                                                        {projectedHealth}/{character.max_health}
                                                        {' '}(+{totalHealing})
                                                    </span>
                                                </div>
                                            )}

                                            {/* Assigned items */}
                                            {assignedItems.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    {assignedItems.map(item => (
                                                        <button
                                                            key={item.id}
                                                            onClick={() => toggleItemAssignment(item, character.id)}
                                                            className="px-2 py-1 bg-green-600/20 border border-green-600 rounded text-xs text-green-400 hover:bg-green-600/30"
                                                        >
                                                            {item.name} (+{item.healing_amount}) ✕
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Available Healing Items */}
                {healingItems.length > 0 && (
                    <div className="mb-6 bg-slate-800 border border-slate-700 rounded-lg p-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Healing Items</h2>
                        <p className="text-gray-400 text-sm mb-4">
                            Click an item, then click a character to assign healing.
                        </p>

                        <div className="space-y-3">
                            {Object.entries(groupedItems).map(([name, items]) => (
                                <div key={name} className="flex items-center justify-between p-3 bg-slate-700 rounded">
                                    <div>
                                        <span className={`font-medium ${getRarityColor(items[0].rarity.value)}`}>
                                            {name}
                                        </span>
                                        <span className="text-green-400 ml-2">+{items[0].healing_amount} HP</span>
                                    </div>
                                    <div className="flex gap-2">
                                        {items.map(item => {
                                            const isAssigned = isItemAssigned(item.id);
                                            return (
                                                <div key={item.id} className="relative">
                                                    <button
                                                        onClick={() => {
                                                            // If not assigned, open selection
                                                            if (!isAssigned) {
                                                                // Auto-assign to first character needing healing
                                                                const needsHealing = healingPreview.characters.find(
                                                                    c => !c.is_incapacitated && c.current_health < c.max_health
                                                                );
                                                                if (needsHealing) {
                                                                    toggleItemAssignment(item, needsHealing.id);
                                                                }
                                                            }
                                                        }}
                                                        disabled={isAssigned}
                                                        className={`px-3 py-1 rounded text-sm transition-colors ${
                                                            isAssigned
                                                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                                                : 'bg-purple-600 hover:bg-purple-700 text-white'
                                                        }`}
                                                    >
                                                        {isAssigned ? 'Assigned' : 'Use'}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        <span className="text-gray-500 text-sm ml-2">
                                            ({items.filter(i => !isItemAssigned(i.id)).length} available)
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-4">
                    <button
                        onClick={handleApplyHealing}
                        disabled={isSubmitting}
                        className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                    >
                        {isSubmitting ? 'Processing...' : 'Apply Healing & Continue'}
                    </button>
                    <button
                        onClick={handleSkipHealing}
                        disabled={isSubmitting}
                        className="px-6 py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                        Skip Healing
                    </button>
                    </div>
                </div>
            </div>
        </>
    );
}
